/**
 * BrokerCockpit — professional-trading-platform layout for the Broker
 * Settings page. Replaces the old backend-admin panel look.
 *
 * Layout (target: 5-second glance answers "which broker, healthy?, cash,
 * live?, switch?"):
 *
 *   Row 1:  Current Broker  |  Account Summary  |  Connection Health
 *   Row 2:  Broker Details  |  Saved Connections
 *   Row 3:  Recent Activity |  Capabilities  |  Advanced Settings (collapsed)
 *
 * All heavy diagnostics + rotate/remove/export live inside the Advanced
 * accordion. The rest of the UI is read-only.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import NicknameEditDialog from "@/components/NicknameEditDialog";
import { notifyBrokerChanged, resetExecutionSourceOverride } from "@/lib/executionSource";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Activity, AlertCircle, CheckCircle2, ChevronDown, ChevronRight,
         Clock, Eye, KeyRound, Loader2, Plus, RefreshCw, ShieldOff, Sparkles, X, Zap } from "lucide-react";

// ─── Small primitives ──────────────────────────────────────────────

const _STATE_MAP = {
  connected:      { color: "text-emerald-400", dot: "bg-emerald-400", border: "border-emerald-500/60", label: "CONNECTED" },
  healthy:        { color: "text-emerald-400", dot: "bg-emerald-400", border: "border-emerald-500/60", label: "CONNECTED" },
  warning:        { color: "text-amber-400",   dot: "bg-amber-400",   border: "border-amber-500/60",   label: "WARNING" },
  needs_attention:{ color: "text-amber-400",   dot: "bg-amber-400",   border: "border-amber-500/60",   label: "WARNING" },
  error:          { color: "text-red-400",     dot: "bg-red-400",     border: "border-red-500/60",     label: "ERROR" },
  disconnected:   { color: "text-muted-foreground", dot: "bg-muted-foreground", border: "border-border/40", label: "DISCONNECTED" },
  saved:          { color: "text-muted-foreground", dot: "bg-muted-foreground", border: "border-border/40", label: "SAVED" },
  not_configured: { color: "text-muted-foreground", dot: "bg-muted-foreground", border: "border-border/40", label: "NOT CONFIGURED" },
  credentials_removed: { color: "text-red-400", dot: "bg-red-400", border: "border-red-500/60", label: "CREDENTIALS REMOVED" },
  unknown:        { color: "text-muted-foreground", dot: "bg-muted-foreground", border: "border-border/40", label: "NOT VERIFIED" },
};

function _resolveState(c) {
  if (!c) return "not_configured";
  if (c.is_active && c.health_status === "healthy") return "connected";
  if (c.is_active && c.health_status === "needs_attention") return "warning";
  if (c.health_status === "credentials_removed") return "credentials_removed";
  if (c.credentials_saved && !c.is_active) return "saved";
  if (!c.credentials_saved && c.broker_type !== "internal_paper_mock") return "not_configured";
  if (c.credentials_saved && c.is_active) return "connected";
  return c.health_status || "unknown";
}

function _fmtWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return `${s} sec ago`;
    if (s < 3600) return `${Math.floor(s/60)} min ago`;
    if (s < 86400) return `${Math.floor(s/3600)} hr ago`;
    return d.toLocaleDateString();
  } catch { return "—"; }
}

function _fmtDollar(v) {
  // Financial account balances always show cents. Never round to whole
  // dollars — the reconciliation panel would then look inconsistent with
  // the cockpit and users could not verify small deltas (e.g. $9,999.97
  // vs. $10,000.00).
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return "$" + Number(v).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function StatePill({ state, size = "sm" }) {
  const m = _STATE_MAP[state] || _STATE_MAP.unknown;
  const sz = size === "lg" ? "text-xs px-2 py-1" : "text-[10px] px-1.5 py-0.5";
  return (
    <span className={`inline-flex items-center gap-1.5 mono uppercase tracking-widest border ${m.border} ${m.color} ${sz}`}
          data-testid={`state-pill-${state}`}>
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${m.dot}`}/>
      {m.label}
    </span>
  );
}

function EnvironmentBadge({ env, brokerType }) {
  // Unified with the top-right execution-source chip: 🟢 Alpaca (any),
  // 🟡 Internal Paper, 🔴 LIVE. `env` remains authoritative for LIVE — if
  // an Alpaca connection is live, red overrides broker-identity green.
  if (!env && !brokerType) return null;
  const isLive = env === "live";
  let m;
  if (isLive) {
    m = { c: "border-red-500/60 text-red-400 bg-red-500/5", d: "🔴", label: "LIVE" };
  } else if (brokerType === "internal_paper_mock" || env === "mock") {
    m = { c: "border-signal-yellow/60 text-signal-yellow bg-signal-yellow/5", d: "🟡", label: "INTERNAL PAPER" };
  } else if (brokerType === "alpaca_paper" || env === "paper") {
    m = { c: "border-emerald-500/60 text-emerald-400 bg-emerald-500/5", d: "🟢", label: "ALPACA PAPER" };
  } else {
    m = { c: "border-border/60 text-muted-foreground", d: "⚪", label: (env || brokerType || "—").toUpperCase() };
  }
  return (
    <span className={`inline-flex items-center gap-1 mono uppercase tracking-widest text-[10px] px-1.5 py-0.5 border ${m.c}`}
          data-testid={`env-badge-${env || brokerType}`}>
      <span>{m.d}</span>{m.label}
    </span>
  );
}

function CardShell({ title, children, className = "", testId, right }) {
  return (
    <div className={`bg-card border border-border/50 p-4 ${className}`} data-testid={testId}>
      {(title || right) && (
        <div className="flex items-center gap-2 mb-3">
          {title && <div className="label-cap">{title}</div>}
          {right && <div className="ml-auto">{right}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

function KVList({ rows, className = "" }) {
  const visible = rows.filter(r => r.value !== undefined && r.value !== null && r.value !== "");
  return (
    <div className={`space-y-1 text-xs ${className}`}>
      {visible.map(r => (
        <div key={r.label} className="flex items-baseline gap-2">
          <div className="text-muted-foreground uppercase tracking-widest text-[10px] mono min-w-[110px]">{r.label}</div>
          <div className={`mono flex-1 text-right ${r.className || ""}`}>{r.value}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Sub-cards ────────────────────────────────────────────────────

function CurrentBrokerCard({ active, brokerAccount, onDisconnect, onSwitch, onHealth, onRefresh, onSync, onRebalance, onEditNickname, busy, syncing, openPositions, openOrders, liveTradingEnabled }) {
  if (!active) return (
    <CardShell title="Current Broker" testId="cb-current">
      <div className="text-xs text-muted-foreground italic">No active connection. Pick one from Saved Connections →</div>
    </CardShell>
  );
  const state = _resolveState(active);
  const env = active.environment || (active.broker_type === "internal_paper_mock" ? "mock" : "paper");
  const brokerLabel = active.broker_type === "alpaca_paper" ? "Alpaca Paper"
                    : active.broker_type === "internal_paper_mock" ? "Internal Paper Mock"
                    : active.display_name;
  const meta = active.broker_metadata || {};
  const primaryLine = active.nickname || brokerLabel;
  const secondaryLine = active.nickname
    ? `${brokerLabel}${active.masked_account_number ? ` · ${active.masked_account_number}` : ""}`
    : (active.masked_account_number ? `${brokerLabel} · ${active.masked_account_number}` : brokerLabel);
  return (
    <CardShell title="Current Broker" testId="cb-current"
      right={<div className="flex items-center gap-1.5"><EnvironmentBadge env={env} brokerType={active.broker_type}/><StatePill state={state}/></div>}>
      <div className="space-y-3">
        <div className="flex items-baseline gap-2 flex-wrap">
          <div className="text-xl mono font-medium" data-testid="cb-current-name">{primaryLine}</div>
          <button onClick={() => onEditNickname && onEditNickname(active)}
                  className="text-[10px] mono uppercase tracking-widest text-muted-foreground hover:text-foreground border border-border/40 px-1.5 py-0.5"
                  title="Edit nickname (WingWise-local, never sent to broker)"
                  data-testid="cb-edit-nickname">Edit</button>
          <div className="text-[10px] mono text-muted-foreground ml-auto" data-testid="cb-secondary-line">
            {secondaryLine}
          </div>
        </div>
        {/* Rich Alpaca-sourced metadata line */}
        {active.broker_type === "alpaca_paper" && (
          <div className="flex items-center gap-2 flex-wrap text-[10px] mono uppercase tracking-widest text-muted-foreground" data-testid="cb-broker-meta">
            {meta.status && <span data-testid="cb-meta-status">{meta.status}</span>}
            {meta.margin_label && <><span>·</span><span data-testid="cb-meta-margin">{meta.margin_label}</span></>}
            {meta.currency && <><span>·</span><span>{meta.currency}</span></>}
            {meta.shorting_enabled != null && <><span>·</span><span>Shorting {meta.shorting_enabled ? "ON" : "OFF"}</span></>}
            {meta.fractional_trading && <><span>·</span><span>Fractional</span></>}
            {meta.pattern_day_trader && <><span>·</span><span className="text-amber-400">PDT</span></>}
            {(meta.trading_blocked || meta.transfers_blocked || meta.account_blocked) && (
              <><span>·</span><span className="text-red-400">
                {meta.account_blocked ? "ACCOUNT BLOCKED" : meta.trading_blocked ? "TRADING BLOCKED" : "TRANSFERS BLOCKED"}
              </span></>
            )}
          </div>
        )}
        {active.needs_rebalance_review && (
          <button onClick={onRebalance}
                  className="w-full border border-amber-500/60 bg-amber-500/5 text-amber-400 text-[11px] mono px-2 py-1.5 flex items-center justify-center gap-2 hover:bg-amber-500/10"
                  data-testid="cb-rebalance-hint">
            <AlertCircle size={12} strokeWidth={1.75}/>
            Portfolio needs rebalance — <span className="underline">review & apply</span>
          </button>
        )}
        {/* Dense 2×3 balance grid */}
        <div className="grid grid-cols-3 gap-2 border border-border/40 p-2" data-testid="cb-account-summary">
          <div>
            <div className="text-[9px] mono uppercase tracking-widest text-muted-foreground">Cash</div>
            <div className="text-sm mono">{brokerAccount?.cash != null ? _fmtDollar(brokerAccount.cash) : "—"}</div>
          </div>
          <div>
            <div className="text-[9px] mono uppercase tracking-widest text-muted-foreground">Equity</div>
            <div className="text-sm mono">{brokerAccount?.equity != null ? _fmtDollar(brokerAccount.equity) : "—"}</div>
          </div>
          <div>
            <div className="text-[9px] mono uppercase tracking-widest text-muted-foreground">Buying Power</div>
            <div className="text-sm mono text-signal-blue">{brokerAccount?.buying_power != null ? _fmtDollar(brokerAccount.buying_power) : "—"}</div>
          </div>
          <div>
            <div className="text-[9px] mono uppercase tracking-widest text-muted-foreground">Margin</div>
            <div className="text-sm mono">{brokerAccount?.multiplier ? `${brokerAccount.multiplier}x` : "1x"}</div>
          </div>
          <div>
            <div className="text-[9px] mono uppercase tracking-widest text-muted-foreground">Positions</div>
            <div className="text-sm mono">{openPositions !== null ? openPositions : "—"}</div>
          </div>
          <div>
            <div className="text-[9px] mono uppercase tracking-widest text-muted-foreground">Orders</div>
            <div className="text-sm mono">{openOrders !== null ? openOrders : "—"}</div>
          </div>
        </div>
        <div className="flex items-center gap-3 text-[10px] mono uppercase tracking-widest">
          <span className="text-muted-foreground">Last Sync <span className="text-foreground">{_fmtWhen(active.last_verified_at || brokerAccount?.last_health_check_at)}</span></span>
          <span className={`ml-auto ${liveTradingEnabled ? "text-red-400" : "text-muted-foreground"}`} data-testid="cb-live-trading">
            Live Trading · {liveTradingEnabled ? "ON" : "OFF"}
          </span>
        </div>
        {/* Actions — 5 compact icon buttons with hover tooltips */}
        <TooltipProvider delayDuration={100}>
          <div className="grid grid-cols-5 gap-1 pt-2 border-t border-border/40">
            <Tooltip>
              <TooltipTrigger asChild>
                <button onClick={onHealth} disabled={busy}
                        className="text-[10px] mono uppercase tracking-widest border border-border/60 hover:border-signal-blue px-1 py-1.5 disabled:opacity-40 flex items-center justify-center gap-1"
                        data-testid="cb-run-health">
                  <Activity size={11} strokeWidth={1.75}/>
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Health Check</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button onClick={onRefresh} disabled={busy}
                        className="text-[10px] mono uppercase tracking-widest border border-border/60 hover:border-signal-blue px-1 py-1.5 disabled:opacity-40 flex items-center justify-center gap-1"
                        data-testid="cb-refresh-balance">
                  <RefreshCw size={11} strokeWidth={1.75}/>
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Refresh Balance</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button onClick={onSync} disabled={syncing}
                        className="text-[10px] mono uppercase tracking-widest border border-border/60 hover:border-signal-blue px-1 py-1.5 disabled:opacity-40 flex items-center justify-center gap-1"
                        data-testid="cb-sync">
                  <Sparkles size={11} strokeWidth={1.75} className={syncing ? "animate-spin" : ""}/>
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Sync Now (reconcile broker ledger)</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button onClick={onSwitch}
                        className="text-[10px] mono uppercase tracking-widest border border-signal-blue text-signal-blue hover:bg-signal-blue/10 px-1 py-1.5 flex items-center justify-center gap-1"
                        data-testid="cb-switch">
                  <Zap size={11} strokeWidth={1.75}/>
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Switch Broker</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button onClick={onDisconnect} disabled={busy}
                        className="text-[10px] mono uppercase tracking-widest border border-red-500/60 text-red-400 hover:bg-red-500/10 px-1 py-1.5 disabled:opacity-40 flex items-center justify-center gap-1"
                        data-testid="cb-disconnect">
                  <X size={11} strokeWidth={1.75}/>
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Disconnect</TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </div>
    </CardShell>
  );
}

function ConnectionHealthCard({ diagnostics, onRefresh, busy }) {
  if (diagnostics?.inactive) return (
    <CardShell title="Connection Health" testId="cb-health">
      <div className="text-xs text-muted-foreground italic">No active connection — connect a broker to run diagnostics.</div>
    </CardShell>
  );
  if (!diagnostics) return (
    <CardShell title="Connection Health" testId="cb-health">
      <div className="text-xs text-muted-foreground italic"><Loader2 size={12} className="inline animate-spin mr-1"/>Running diagnostics…</div>
    </CardShell>
  );
  const CHECK_LABELS = {
    broker_reachable:   "Broker Reachable",
    credentials_valid:  "Credentials Valid",
    rest_api:           "REST API",
    streaming_api:      "Streaming API",
    market_data:        "Market Data",
    order_endpoint:     "Order Endpoint",
    portfolio_endpoint: "Portfolio Endpoint",
    positions:          "Positions",
  };
  const badge = (s) => s === "pass" ? <CheckCircle2 size={12} strokeWidth={2} className="text-emerald-400"/>
              : s === "fail" ? <X size={12} strokeWidth={2} className="text-red-400"/>
              : <span className="text-muted-foreground text-[10px] mono">—</span>;

  return (
    <CardShell title="Connection Health" testId="cb-health"
      right={<button onClick={onRefresh} disabled={busy}
                     className="text-[9px] mono uppercase tracking-widest text-muted-foreground hover:text-signal-blue disabled:opacity-40"
                     data-testid="cb-health-refresh">
        {busy ? <Loader2 size={10} className="inline animate-spin"/> : "Refresh"}
      </button>}>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs" data-testid="cb-health-checks">
        {(diagnostics.checks || []).map(c => (
          <div key={c.name} className="flex items-center gap-2">
            <div className="w-4">{badge(c.status)}</div>
            <div className="flex-1">{CHECK_LABELS[c.name] || c.name}</div>
            {c.detail && <div className="text-[9px] mono text-muted-foreground truncate max-w-[100px]" title={c.detail}>{c.detail}</div>}
          </div>
        ))}
      </div>
      <div className="pt-2 mt-2 border-t border-border/40 text-[10px] mono uppercase tracking-widest text-muted-foreground flex items-center justify-between">
        <div>Last Check {_fmtWhen(diagnostics.last_health_check_at)}</div>
        {diagnostics.latency_ms != null && (
          <div>Latency <span className="text-foreground">{diagnostics.latency_ms.toFixed(0)} ms</span></div>
        )}
      </div>
    </CardShell>
  );
}

function BrokerDetailsCard({ active, brokerAccount }) {
  if (!active) return null;
  const env = active.environment || "—";
  const bt = active.broker_type === "internal_paper_mock" ? "Internal Mock"
           : active.broker_type === "alpaca_paper" ? "Alpaca" : active.broker_type;
  const rows = [
    { label: "Provider",         value: bt },
    { label: "Environment",      value: env.toUpperCase() },
    { label: "Connection",       value: _STATE_MAP[_resolveState(active)]?.label || "—" },
    { label: "Account",          value: active.masked_account_id || (brokerAccount?.account_id ? brokerAccount.account_id : null) },
    { label: "Account Status",   value: brokerAccount?.account_status },
    { label: "Cash",             value: _fmtDollar(brokerAccount?.cash) },
    { label: "Equity",           value: _fmtDollar(brokerAccount?.equity) },
    { label: "Buying Power",     value: _fmtDollar(brokerAccount?.buying_power) },
    { label: "Margin Profile",   value: brokerAccount?.multiplier ? `Reg-T ${brokerAccount.multiplier}x` : "Cash / 1x" },
    { label: "Connected Since",  value: brokerAccount?.connected_at ? new Date(brokerAccount.connected_at).toLocaleTimeString() : null },
    { label: "Last Verified",    value: active.last_verified_at ? new Date(active.last_verified_at).toLocaleTimeString() : null },
  ];
  return (
    <CardShell title="Broker Details" testId="cb-details">
      <KVList rows={rows}/>
    </CardShell>
  );
}

function SavedConnectionsCard({ connections, active, onActivate, onAdd, busyId, addOpen, addForm, onEditNickname }) {
  const rows = connections || [];
  return (
    <CardShell title="Broker Connections" testId="cb-saved"
      right={<button onClick={onAdd}
                     className="text-[10px] mono uppercase tracking-widest border border-signal-blue text-signal-blue px-2 py-1 hover:bg-signal-blue/10"
                     data-testid="cb-add-toggle">
        <Plus size={11} strokeWidth={1.75} className="inline mr-1"/> Add Connection
      </button>}>
      {addOpen && addForm}
      <div className="space-y-2" data-testid="cb-saved-list">
        {rows.map(c => {
          const state = _resolveState(c);
          const env = c.environment;
          const brokerLabel = c.broker_type === "alpaca_paper" ? "Alpaca Paper"
                            : c.broker_type === "internal_paper_mock" ? "Internal Paper"
                            : c.display_name;
          const primary = c.nickname || brokerLabel;
          return (
            <div key={c.connection_id}
                 className={`border p-3 space-y-1 ${c.is_active ? "border-emerald-500/60 bg-emerald-500/5" : "border-border/50"}`}
                 data-testid={`cb-conn-${c.broker_type}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${_STATE_MAP[state]?.dot || "bg-muted-foreground"}`}/>
                <div className="text-sm mono font-medium" data-testid={`cb-primary-${c.broker_type}`}>{primary}</div>
                <EnvironmentBadge env={env} brokerType={c.broker_type}/>
                <StatePill state={state}/>
                <button onClick={() => onEditNickname && onEditNickname(c)}
                        className="text-[9px] mono uppercase tracking-widest text-muted-foreground hover:text-foreground border border-border/40 px-1 py-0.5"
                        title="Edit nickname"
                        data-testid={`cb-edit-nickname-${c.broker_type}`}>Edit</button>
                <div className="ml-auto text-[10px] mono text-muted-foreground">
                  {c.nickname ? `${brokerLabel} · ` : ""}{c.masked_account_number || c.masked_account_id || "—"}
                </div>
              </div>
              <div className="text-[10px] mono text-muted-foreground">
                {c.is_active
                  ? <>Active · Last Verified {_fmtWhen(c.last_verified_at)}</>
                  : <>Last Used {_fmtWhen(c.last_connected_at)}</>}
              </div>
              {!c.is_active && (
                <div className="flex gap-1.5 pt-1">
                  <button onClick={() => onActivate(c.connection_id)}
                          disabled={busyId === c.connection_id ||
                                    (c.broker_type !== "internal_paper_mock" && !c.credentials_saved)}
                          className="text-[10px] mono uppercase tracking-widest border border-signal-blue text-signal-blue hover:bg-signal-blue/10 px-2 py-1 disabled:opacity-40"
                          data-testid={`cb-connect-${c.broker_type}`}>
                    <Zap size={10} strokeWidth={1.75} className="inline mr-1"/>
                    {c.credentials_saved || c.broker_type === "internal_paper_mock" ? "Connect" : "Reconnect (enter keys)"}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </CardShell>
  );
}

function RecentActivityCard({ activity }) {
  if (activity?.inactive) return (
    <CardShell title="Recent Activity" testId="cb-activity">
      <div className="text-xs text-muted-foreground italic">No active connection — activity resumes after reconnect.</div>
    </CardShell>
  );
  if (!activity) return (
    <CardShell title="Recent Activity" testId="cb-activity">
      <div className="text-xs text-muted-foreground italic"><Loader2 size={12} className="inline animate-spin mr-1"/>Loading…</div>
    </CardShell>
  );
  const events = activity.events || [];
  if (!events.length) return (
    <CardShell title="Recent Activity" testId="cb-activity">
      <div className="text-xs text-muted-foreground italic">No recent events.</div>
    </CardShell>
  );
  const LABEL_MAP = {
    "broker_connection_added":              "Connection Added",
    "broker_connection_activated":          "Connected",
    "broker_connection_disconnected":       "Disconnected",
    "broker_connection_keys_rotated":       "Keys Rotated",
    "broker_connection_credentials_removed":"Credentials Removed",
    "broker_connection_credentials_removed_noop":"Credentials Removed (noop)",
    "broker_connection_credentials_updated":"Credentials Updated",
    "broker_connection_migrated_from_legacy":"Migrated From Legacy",
  };
  return (
    <CardShell title="Recent Activity" testId="cb-activity">
      <div className="space-y-1.5 text-xs">
        {events.slice(0, 10).map((e, i) => (
          <div key={i} className="flex items-baseline gap-2 border-b border-border/20 pb-1 last:border-0">
            <Clock size={10} strokeWidth={1.75} className="text-muted-foreground shrink-0 mt-1"/>
            <div className="text-[10px] mono text-muted-foreground min-w-[76px]">
              {_fmtWhen(e.timestamp)}
            </div>
            <div className="flex-1">{LABEL_MAP[e.event] || e.event}</div>
          </div>
        ))}
      </div>
    </CardShell>
  );
}

function CapabilitiesCard({ capabilities }) {
  const entries = Object.entries(capabilities || {});
  if (entries.length === 0) return (
    <CardShell title="Capabilities" testId="cb-capabilities">
      <div className="text-xs text-muted-foreground italic">No active connection — capabilities depend on the connected broker.</div>
    </CardShell>
  );
  const LABEL_MAP = {
    long: "Long Positions", short: "Short Selling", fractional: "Fractional Shares",
    bracket_orders: "Bracket Orders", trailing_stops: "Trailing Stops",
    streaming: "Streaming", market_orders: "Market Orders",
    limit_orders: "Limit Orders", stop_orders: "Stop Orders",
    options: "Options",
  };
  return (
    <CardShell title="Capabilities" testId="cb-capabilities">
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs" data-testid="cb-capabilities-grid">
        {entries.map(([k, v]) => (
          <div key={k} className="flex items-center gap-1.5">
            {v ? <CheckCircle2 size={11} strokeWidth={2} className="text-emerald-400"/>
               : <span className="text-[10px] text-muted-foreground">—</span>}
            <span className={v ? "" : "text-muted-foreground line-through"}>{LABEL_MAP[k] || k}</span>
            {!v && k === "options" && <span className="text-[9px] mono text-muted-foreground">(future)</span>}
          </div>
        ))}
      </div>
    </CardShell>
  );
}

function MockCapitalCard({ active, brokerAccount, onSave }) {
  const isMock = active?.broker_type === "internal_paper_mock";
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  if (!isMock) return null;
  const submit = async () => {
    const n = Number(String(amount).replace(/[$,]/g, ""));
    if (!n || n <= 0) { toast.error("Enter a positive amount"); return; }
    setBusy(true);
    try { await onSave(n); setAmount(""); toast.success(`Mock capital reset to ${_fmtDollar(n)}`); }
    finally { setBusy(false); }
  };
  const currentEquity = brokerAccount?.equity;
  const currentStart  = brokerAccount?.starting_equity;
  return (
    <CardShell title="Mock Starting Capital" testId="cb-mock-capital">
      <div className="text-[11px] text-muted-foreground mb-2">
        Currently <span className="text-foreground mono">{_fmtDollar(currentStart ?? currentEquity)}</span>.
        Reset the Internal Paper Mock to any amount that matches your real paper broker (e.g. $10,000) so risk sizing feels realistic. This resets cash, equity, and buying power together.
      </div>
      <div className="flex gap-2">
        <input type="text" placeholder={`e.g. ${(currentStart ?? currentEquity ?? 10000) | 0}`} value={amount}
                onChange={e => setAmount(e.target.value)}
                className="flex-1 bg-transparent border border-border/60 px-2 py-1 text-xs mono"
                data-testid="cb-mock-capital-input"/>
        <button onClick={submit} disabled={busy}
                 className="text-[10px] mono uppercase tracking-widest border border-signal-blue text-signal-blue hover:bg-signal-blue/10 px-2 py-1 disabled:opacity-40"
                 data-testid="cb-mock-capital-save">
          {busy ? "…" : "Reset"}
        </button>
      </div>
    </CardShell>
  );
}


function AddConnectionInline({ onCancel, onSuccess }) {
  const [broker, setBroker] = useState("alpaca_paper");
  const [key, setKey] = useState("");
  const [secret, setSecret] = useState("");
  const [save, setSave] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (broker === "alpaca_paper" && (!key.trim() || !secret.trim())) {
      toast.error("API Key and Secret are required"); return;
    }
    setBusy(true);
    try {
      await api.post("/broker/connections", {
        broker_type: broker, api_key: key.trim(),
        api_secret: secret.trim(), save_credentials: save,
      });
      toast.success(save ? "Connection saved — verified and ready" : "Connection verified (not saved)");
      setKey(""); setSecret("");
      onSuccess?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-signal-blue/40 p-3 space-y-2 mb-3" data-testid="cb-add-form">
      <div className="flex items-center gap-2">
        <div className="text-[10px] mono uppercase tracking-widest text-signal-blue">Choose Broker</div>
        <button onClick={onCancel} className="ml-auto text-muted-foreground hover:text-foreground" data-testid="cb-add-cancel">
          <X size={13} strokeWidth={1.75}/>
        </button>
      </div>
      <div className="grid grid-cols-1 gap-1 text-xs">
        {[
          { id: "alpaca_paper",         label: "Alpaca Paper",         disabled: false },
          { id: "internal_paper_mock",  label: "Internal Paper",       disabled: false },
          { id: "alpaca_live",          label: "Alpaca Live",          disabled: true },
          { id: "interactive_brokers",  label: "Interactive Brokers",  disabled: true },
          { id: "schwab",               label: "Schwab",               disabled: true },
        ].map(o => (
          <label key={o.id}
            className={`flex items-center gap-2 border px-2 py-1.5 cursor-pointer ${
              o.disabled ? "border-border/30 text-muted-foreground cursor-not-allowed" :
              broker === o.id ? "border-signal-blue text-signal-blue" : "border-border/50 hover:border-signal-blue/60"
            }`}>
            <input type="radio" name="broker" value={o.id} disabled={o.disabled}
                   checked={broker === o.id} onChange={e => setBroker(e.target.value)}
                   data-testid={`cb-add-broker-${o.id}`}/>
            <span>{o.label}</span>
            {o.disabled && <span className="ml-auto text-[9px] uppercase mono">Coming Soon</span>}
          </label>
        ))}
      </div>
      {broker === "alpaca_paper" && (
        <>
          <input type="text" placeholder="API Key ID" value={key} onChange={e => setKey(e.target.value)}
                 className="w-full bg-transparent border border-border/60 px-2 py-1 text-xs mono"
                 data-testid="cb-add-key" autoComplete="off"/>
          <input type="password" placeholder="Secret Key" value={secret} onChange={e => setSecret(e.target.value)}
                 className="w-full bg-transparent border border-border/60 px-2 py-1 text-xs mono"
                 data-testid="cb-add-secret" autoComplete="new-password"/>
          <label className="flex items-center gap-2 text-[11px] cursor-pointer">
            <input type="checkbox" checked={save} onChange={e => setSave(e.target.checked)}
                   data-testid="cb-add-save"/>
            Save these credentials securely for future use
          </label>
        </>
      )}
      <button onClick={submit} disabled={busy}
              className="w-full border border-signal-blue text-signal-blue hover:bg-signal-blue/10 text-[11px] mono uppercase tracking-widest px-2 py-2 disabled:opacity-40"
              data-testid="cb-add-submit">
        {busy ? "Verifying…" : "Verify & Save"}
      </button>
    </div>
  );
}

// ─── Advanced Settings accordion (rotate / remove / rename / export) ─

function AdvancedSettings({ active, onRotateSubmit, onRemove }) {
  const [open, setOpen] = useState(false);
  const [rotOpen, setRotOpen] = useState(false);
  const [key, setKey] = useState("");
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);

  const submitRotate = async () => {
    if (!key.trim() || !secret.trim()) { toast.error("API Key + Secret required"); return; }
    setBusy(true);
    try { await onRotateSubmit(key.trim(), secret.trim()); setKey(""); setSecret(""); setRotOpen(false); }
    finally { setBusy(false); }
  };

  const canRotate = active?.broker_type === "alpaca_paper" && active.credentials_saved;
  const canRemove = active?.broker_type === "alpaca_paper" && active.credentials_saved;

  return (
    <div className="bg-card border border-border/50" data-testid="cb-advanced">
      <button onClick={() => setOpen(v => !v)}
              className="w-full flex items-center gap-2 px-4 py-3 hover:bg-secondary/20"
              data-testid="cb-advanced-toggle">
        {open ? <ChevronDown size={13}/> : <ChevronRight size={13}/>}
        <div className="label-cap">Advanced Settings</div>
        <div className="ml-auto text-[10px] mono uppercase tracking-widest text-muted-foreground">
          Rotate · Delete · Diagnostics
        </div>
      </button>
      {open && (
        <div className="p-4 pt-0 space-y-3">
          <div className="text-[11px] text-muted-foreground">
            Advanced actions for the ACTIVE connection. Most users never touch these.
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button onClick={() => setRotOpen(v => !v)} disabled={!canRotate}
                    className="text-[10px] mono uppercase tracking-widest border border-amber-500/60 text-amber-400 hover:bg-amber-500/10 px-2 py-1 disabled:opacity-40"
                    data-testid="cb-rotate-toggle">
              <KeyRound size={10} strokeWidth={1.75} className="inline mr-1"/> Rotate Keys
            </button>
            <button onClick={onRemove} disabled={!canRemove}
                    className="text-[10px] mono uppercase tracking-widest border border-red-500/60 text-red-400 hover:bg-red-500/10 px-2 py-1 disabled:opacity-40"
                    data-testid="cb-remove">
              <ShieldOff size={10} strokeWidth={1.75} className="inline mr-1"/> Delete Connection
            </button>
            <button disabled
                    className="text-[10px] mono uppercase tracking-widest border border-border/50 text-muted-foreground px-2 py-1 disabled:opacity-60"
                    data-testid="cb-export">
              <Eye size={10} strokeWidth={1.75} className="inline mr-1"/> Export Connection Info
            </button>
          </div>

          {rotOpen && (
            <div className="border border-amber-500/40 p-3 space-y-2" data-testid="cb-rotate-form">
              <div className="text-[10px] text-muted-foreground">
                Enter NEW keys. Old keys stay active until the new ones verify — no risk of being locked out.
              </div>
              <input type="text" placeholder="New API Key ID" value={key} onChange={e => setKey(e.target.value)}
                     className="w-full bg-transparent border border-border/60 px-2 py-1 text-xs mono"
                     data-testid="cb-rotate-key"/>
              <input type="password" placeholder="New Secret" value={secret} onChange={e => setSecret(e.target.value)}
                     className="w-full bg-transparent border border-border/60 px-2 py-1 text-xs mono"
                     data-testid="cb-rotate-secret"/>
              <button onClick={submitRotate} disabled={busy}
                      className="w-full border border-amber-500 text-amber-400 hover:bg-amber-500/10 text-[11px] mono uppercase tracking-widest px-2 py-2 disabled:opacity-40"
                      data-testid="cb-rotate-submit">
                {busy ? "Verifying…" : "Verify & Rotate"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Root component ───────────────────────────────────────────────

export default function BrokerCockpit({ brokerAccount, onBrokerSwitch }) {
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [diagnostics, setDiagnostics] = useState(null);
  const [activity, setActivity] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);
  const [openPositions, setOpenPositions] = useState(null);
  const [openOrders, setOpenOrders] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/broker/connections");
      setData(data);
      const active = data?.active;
      if (active) {
        // Fetch diagnostics + activity in parallel.
        const [d, a] = await Promise.all([
          api.get(`/broker/connections/${active.connection_id}/diagnostics`).then(r => r.data).catch(() => null),
          api.get(`/broker/connections/${active.connection_id}/activity`).then(r => r.data).catch(() => null),
        ]);
        setDiagnostics(d);
        setActivity(a);
      } else {
        // Explicitly clear so the child cards render empty-state, not "Loading…".
        setDiagnostics({ inactive: true });
        setActivity({ inactive: true, events: [] });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load broker cockpit");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Reload the cockpit whenever a broker action happens elsewhere on the
  // page — e.g. the rebalance preview modal clearing the needs_rebalance_review
  // flag. Prevents stale "portfolio needs rebalance" banners after apply/close.
  useEffect(() => {
    const h = () => load();
    window.addEventListener("tm:broker-changed", h);
    return () => window.removeEventListener("tm:broker-changed", h);
  }, [load]);

  // Fetch light account rollups.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const p = await api.get("/positions").then(r => r.data);
        if (alive) setOpenPositions(Array.isArray(p) ? p.filter(x => !x.closed_at).length : 0);
      } catch { if (alive) setOpenPositions(null); }
      try {
        const o = await api.get("/order-intents").then(r => r.data);
        const arr = Array.isArray(o) ? o : (o?.items || o?.records || []);
        if (alive) setOpenOrders(arr.filter(x => !["FILLED","CANCELLED","REJECTED"].includes((x.status || "").toUpperCase())).length);
      } catch { if (alive) setOpenOrders(null); }
    })();
    return () => { alive = false; };
  }, [data?.active?.connection_id]);

  const active = data?.active;
  const capabilities = active?.capabilities || {};

  const activate = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/broker/connections/${id}/activate`);
      toast.success("Connection activated");
      resetExecutionSourceOverride();
      // Do NOT pass a mode string here — the chip must refetch /broker/status
      // to pick up the actual new mode (e.g. "alpaca_paper"). Passing the
      // literal "switched" set the chip's brokerMode to "switched" and broke
      // auto-follow.
      notifyBrokerChanged();
      onBrokerSwitch?.();
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Activation failed";
      toast.error(msg);
    } finally { setBusyId(null); }
  };

  const runHealth = async () => {
    if (!active) return;
    setDiagBusy(true);
    try {
      const { data: d } = await api.get(`/broker/connections/${active.connection_id}/diagnostics`);
      setDiagnostics(d);
      toast[d.overall === "healthy" ? "success" : "error"](
        d.overall === "healthy" ? "All checks passed" : `Health: ${d.overall}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Health failed"); }
    finally { setDiagBusy(false); }
  };

  const refreshBalance = async () => {
    if (!active) return;
    setDiagBusy(true);
    try {
      const { data: r } = await api.post(`/broker/connections/${active.connection_id}/refresh-balance`);
      toast.success(`Balance refreshed · Cash ${_fmtDollar(r.cash)} · Equity ${_fmtDollar(r.equity)}`);
      await load();
      onBrokerSwitch?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally { setDiagBusy(false); }
  };

  const [syncing, setSyncing] = useState(false);
  const runSync = async () => {
    setSyncing(true);
    try {
      await api.post("/broker/sync");
      toast.success("Broker synced");
      await load();
      onBrokerSwitch?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  const setMockCapital = async (amount) => {
    if (!active) return;
    await api.post(`/broker/connections/${active.connection_id}/mock-capital`,
                    { amount });
    await load();
    onBrokerSwitch?.();
  };

  const openRebalance = () => {
    window.dispatchEvent(new Event("tm:open-rebalance"));
  };

  const disconnect = async () => {
    if (!active) return;
    const ok = await confirm.confirm({
      title: "Disconnect connection?",
      description: "Stops using this broker but PRESERVES your saved credentials so you can reconnect with one click.",
      confirmLabel: "Disconnect",
      testId: "cb-disconnect-confirm",
    });
    if (!ok) return;
    try {
      await api.post(`/broker/connections/${active.connection_id}/disconnect`);
      toast.success("Disconnected — credentials preserved");
      // Notify sidebar / chip / other panels to refetch broker status.
      notifyBrokerChanged();
      onBrokerSwitch?.();
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Disconnect failed"); }
  };

  const [nicknameEditFor, setNicknameEditFor] = useState(null);
  const editNickname = (conn) => {
    if (!conn) return;
    setNicknameEditFor(conn);
  };
  const saveNickname = async (next) => {
    const conn = nicknameEditFor;
    if (!conn) return;
    try {
      await api.patch(`/broker/connections/${conn.connection_id}`, {
        nickname: next,
      });
      toast.success(next
        ? `Nickname set: ${next}`
        : "Nickname cleared");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nickname update failed");
    }
  };



  const rotate = async (key, secret) => {
    if (!active) return;
    try {
      await api.post(`/broker/connections/${active.connection_id}/rotate-keys`,
                     { api_key: key, api_secret: secret });
      toast.success("Keys rotated — session preserved");
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Rotate failed";
      toast.error(msg.startsWith("rotation_verify_failed") ? "New keys failed verification — old keys unchanged" : msg);
    }
  };

  const removeCreds = async () => {
    if (!active) return;
    const ok = await confirm.confirm({
      variant: "destructive",
      title: `Delete ${active.display_name}?`,
      description: "Wipes the encrypted API key & secret. You'll need to enter them again to reconnect. Audit history is preserved.",
      confirmLabel: "Delete Connection",
      testId: "cb-remove-confirm",
    });
    if (!ok) return;
    try {
      await api.delete(`/broker/connections/${active.connection_id}/credentials`);
      toast.success("Credentials removed");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
  };

  const switchBroker = () => {
    // "Switch" scrolls the Saved Connections card into view so the user can
    // pick one — one click on a saved connection is all that's needed.
    document.querySelector('[data-testid="cb-saved"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
    toast.info("Pick a saved connection to switch to");
  };

  const addForm = (
    <AddConnectionInline
      onCancel={() => setAddOpen(false)}
      onSuccess={async () => { setAddOpen(false); await load(); }}/>
  );

  const savedNonActive = useMemo(
    () => (data?.connections || []).filter(c => !c.is_active), [data]);

  const liveTradingEnabled = brokerAccount?.mode === "alpaca_paper"
      && !!brokerAccount?.live_trading_enabled;

  if (!data) return (
    <div className="p-4 text-xs text-muted-foreground bg-card border border-border/50">
      <Loader2 size={12} className="inline animate-spin mr-1"/>Loading broker cockpit…
    </div>
  );

  return (
    <div className="space-y-3" data-testid="broker-cockpit">
      {/* Row 1: dense hero — Current Broker (compact) · Connection Health (wide) */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
        <div className="lg:col-span-2 space-y-3">
          <CurrentBrokerCard active={active} brokerAccount={brokerAccount}
                              onDisconnect={disconnect} onSwitch={switchBroker}
                              onHealth={runHealth} onRefresh={refreshBalance}
                              onSync={runSync} onRebalance={openRebalance}
                              onEditNickname={editNickname}
                              syncing={syncing}
                              busy={diagBusy}
                              openPositions={openPositions}
                              openOrders={openOrders}
                              liveTradingEnabled={liveTradingEnabled}/>
          <MockCapitalCard active={active} brokerAccount={brokerAccount} onSave={setMockCapital}/>
        </div>
        <div className="lg:col-span-3">
          <ConnectionHealthCard diagnostics={diagnostics} onRefresh={runHealth} busy={diagBusy}/>
        </div>
      </div>

      {/* Row 2: Saved Connections (wide) · Broker Details (compact) */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
        <div className="lg:col-span-3">
          <SavedConnectionsCard
            connections={data?.connections || []}
            active={active}
            onActivate={activate}
            onAdd={() => setAddOpen(v => !v)}
            onEditNickname={editNickname}
            busyId={busyId}
            addOpen={addOpen}
            addForm={addForm}/>
        </div>
        <div className="lg:col-span-2">
          <BrokerDetailsCard active={active} brokerAccount={brokerAccount}/>
        </div>
      </div>

      {/* Row 3: Recent Activity · Capabilities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <RecentActivityCard activity={activity}/>
        <CapabilitiesCard capabilities={capabilities}/>
      </div>

      {/* Advanced Settings (collapsed by default) */}
      <AdvancedSettings active={active} onRotateSubmit={rotate} onRemove={removeCreds}/>

      {/* Standard nickname editor — replaces window.prompt. */}
      <NicknameEditDialog
        open={!!nicknameEditFor}
        onClose={() => setNicknameEditFor(null)}
        initialValue={nicknameEditFor?.nickname || ""}
        title="Edit Broker Nickname"
        description="WingWise-local label for this connection — never sent to the broker."
        maxLength={50}
        onSave={saveNickname}/>
    </div>
  );
}
